import { AuthService } from '@/app/core/services/auth.service';
import { WfmNotificationDto, WfmNotificationState } from '@/app/core/models/wfm-notification.models';
import { WfmNotificationApiService } from '@/app/core/services/wfm-notification-api.service';
import { LayoutService } from '@/app/layout/service/layout.service';
import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';

interface NotificationTab {
    id: WfmNotificationState;
    labelKey: string;
}

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, TranslateModule, StyleClassModule, InputTextModule, ButtonModule, IconFieldModule, InputIconModule, RippleModule, AvatarModule],
    template: `<div class="layout-topbar">
        <div class="topbar-left">
            <button tabindex="0" #menubutton type="button" class="menu-button" [attr.aria-label]="'TOPBAR.MENU_TOGGLE_ARIA' | translate" (click)="onMenuButtonClick()">
                <i class="pi pi-chevron-left" aria-hidden="true"></i>
            </button>
            <span class="topbar-separator"></span>
        </div>

        <div class="topbar-right">
            <ul class="topbar-menu">
                <li class="right-sidebar-item">
                    <button type="button" class="right-sidebar-button" [attr.aria-label]="'TOPBAR.SEARCH_ARIA' | translate" (click)="toggleSearchBar()"><i class="pi pi-search" aria-hidden="true"></i></button>
                </li>
                <li class="right-sidebar-item static sm:relative">
                    <button
                        type="button"
                        class="right-sidebar-button relative z-50"
                        [attr.aria-label]="'TOPBAR.NOTIFICATIONS_ARIA' | translate"
                        pStyleClass="@next"
                        enterFromClass="hidden"
                        enterActiveClass="animate-scalein"
                        leaveActiveClass="animate-fadeout"
                        leaveToClass="hidden"
                        [hideOnOutsideClick]="true"
                        (click)="loadNotifications()"
                    >
                        @if (unreadCount() > 0) {
                            <span class="absolute top-2 right-2.5 h-2 w-2 rounded-full bg-red-500" aria-hidden="true"></span>
                        }
                        <i class="pi pi-bell" aria-hidden="true"></i>
                    </button>
                    <div class="list-none m-0 rounded-2xl border border-surface absolute bg-surface-0 dark:bg-surface-900 overflow-hidden hidden origin-top min-w-80 sm:w-96 mt-2 z-50 top-auto shadow-xl" style="right: -100px">
                        <div class="p-4 flex items-center justify-between border-b border-surface">
                            <span class="label-small text-surface-950 dark:text-surface-0">{{ 'TOPBAR.NOTIFICATIONS_TITLE' | translate }}</span>
                            <button type="button" pRipple class="py-1 px-2 text-surface-950 dark:text-surface-0 label-x-small hover:bg-emphasis border border-surface rounded-lg" [disabled]="unreadCount() === 0 || loading()" (click)="markAllRead()">
                                {{ 'TOPBAR.MARK_ALL_READ' | translate }}
                            </button>
                        </div>
                        <div class="flex items-center border-b border-surface" role="tablist">
                            @for (tab of notificationTabs; track tab.id) {
                                <button
                                    type="button"
                                    role="tab"
                                    [attr.aria-selected]="selectedNotificationState() === tab.id"
                                    [class.border-surface-950]="selectedNotificationState() === tab.id"
                                    class="px-3.5 py-2 inline-flex items-center border-b border-transparent gap-2"
                                    (click)="selectNotificationState(tab.id)"
                                >
                                    <span class="label-small">{{ tab.labelKey | translate }}</span>
                                    @if (tab.id === 'unread' && unreadCount() > 0) {
                                        <span class="rounded-full bg-red-500 px-1.5 text-xs text-white">{{ unreadCount() }}</span>
                                    }
                                </button>
                            }
                        </div>
                        @if (loading()) {
                            <div class="p-8 text-center text-sm text-surface-500">{{ 'TOPBAR.NOTIFICATIONS_LOADING' | translate }}</div>
                        } @else if (loadError()) {
                            <div class="p-6 text-center text-sm text-red-600">{{ 'TOPBAR.NOTIFICATIONS_ERROR' | translate }}</div>
                        } @else if (notifications().length === 0) {
                            <div class="p-8 text-center text-sm text-surface-500">{{ 'TOPBAR.NOTIFICATIONS_EMPTY' | translate }}</div>
                        } @else {
                            <ul class="flex max-h-96 flex-col divide-y divide-(--surface-border) overflow-auto">
                                @for (item of notifications(); track item.id) {
                                    <li class="group flex items-start gap-2 px-4 py-3 hover:bg-emphasis">
                                        <button type="button" class="flex min-w-0 flex-1 items-start gap-3 text-left" (click)="openNotification(item)">
                                            <span class="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white" [style.background]="item.color || '#64748b'"><i [class]="item.icon || 'pi pi-bell'"></i></span>
                                            <span class="flex min-w-0 flex-col">
                                                <span class="label-small text-surface-950 dark:text-surface-0">{{ item.title }}</span>
                                                <span class="label-xsmall line-clamp-2">{{ item.text || ('TOPBAR.NOTIFICATIONS_NO_TEXT' | translate) }}</span>
                                                <time class="label-xsmall text-surface-500">{{ item.createdAt | date: 'short' }}</time>
                                            </span>
                                            @if (!item.isRead) {
                                                <span class="mt-2 h-2 w-2 shrink-0 rounded-full bg-red-500" [attr.aria-label]="'TOPBAR.NOTIFICATION_UNREAD' | translate"></span>
                                            }
                                        </button>
                                        @if (!item.isArchived) {
                                            <button type="button" class="mt-1 rounded p-2 text-surface-500 hover:bg-surface-200" [attr.aria-label]="'TOPBAR.ARCHIVE_NOTIFICATION' | translate" (click)="archiveNotification($event, item)">
                                                <i class="pi pi-box"></i>
                                            </button>
                                        }
                                    </li>
                                }
                            </ul>
                        }
                    </div>
                </li>
                <li class="profile-item static sm:relative">
                    <button
                        type="button"
                        class="right-sidebar-button relative z-50"
                        [attr.aria-label]="'TOPBAR.PROFILE_ARIA' | translate"
                        pStyleClass="@next"
                        enterFromClass="hidden"
                        enterActiveClass="animate-scalein"
                        leaveActiveClass="animate-fadeout"
                        leaveToClass="hidden"
                        [hideOnOutsideClick]="true"
                    >
                        @let profilePhoto = authService.currentUser()?.userPhotoUrl?.trim();
                        <p-avatar styleClass="w-10! h-10!">
                            @if (profilePhoto) {
                                <img [src]="profilePhoto" [attr.alt]="'TOPBAR.PROFILE_PHOTO_ALT' | translate" />
                            } @else {
                                <i class="pi pi-user text-xl text-surface-600 dark:text-surface-300" aria-hidden="true"></i>
                            }
                        </p-avatar>
                    </button>
                    <div class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-52 mt-2 right-0 z-999 top-auto shadow-xl">
                        <ul class="flex flex-col gap-1">
                            <li>
                                <a [routerLink]="['/users/edit/00000000-0000-0000-0000-000000000000']" class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis cursor-pointer"
                                    ><i class="pi pi-user"></i><span>{{ authService.currentUser()?.unique_name }}</span></a
                                >
                            </li>
                            <li>
                                <a (click)="logout()" class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis cursor-pointer"
                                    ><i class="pi pi-power-off"></i><span>{{ 'COMMON.LOGOUT' | translate }}</span></a
                                >
                            </li>
                        </ul>
                    </div>
                </li>
            </ul>
        </div>
    </div>`
})
export class AppTopbar implements OnInit {
    readonly layoutService = inject(LayoutService);
    readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly notificationApi = inject(WfmNotificationApiService);
    private notificationRequestId = 0;

    readonly notificationTabs: NotificationTab[] = [
        { id: 'unread', labelKey: 'TOPBAR.NOTIFICATIONS_INBOX' },
        { id: 'all', labelKey: 'TOPBAR.NOTIFICATIONS_ALL' },
        { id: 'archived', labelKey: 'TOPBAR.NOTIFICATIONS_ARCHIVED' }
    ];
    readonly selectedNotificationState = signal<WfmNotificationState>('unread');
    readonly notifications = signal<WfmNotificationDto[]>([]);
    readonly unreadCount = signal(0);
    readonly loading = signal(false);
    readonly loadError = signal(false);
    readonly isDarkTheme = computed(() => this.layoutService.isDarkTheme());

    @ViewChild('menubutton') menuButton!: ElementRef;

    ngOnInit(): void {
        this.loadNotifications();
    }

    loadNotifications(): void {
        const state = this.selectedNotificationState();
        const requestId = ++this.notificationRequestId;
        this.loading.set(true);
        this.loadError.set(false);
        this.notificationApi.list(state, 1, 20).subscribe({
            next: (response) => {
                if (requestId !== this.notificationRequestId || state !== this.selectedNotificationState()) {
                    return;
                }

                this.notifications.set(response.data?.items ?? []);
                this.unreadCount.set(response.data?.unreadCount ?? 0);
                this.loading.set(false);
            },
            error: () => {
                if (requestId !== this.notificationRequestId || state !== this.selectedNotificationState()) {
                    return;
                }

                this.loading.set(false);
                this.loadError.set(true);
            }
        });
    }

    selectNotificationState(state: WfmNotificationState): void {
        this.selectedNotificationState.set(state);
        this.loadNotifications();
    }

    openNotification(item: WfmNotificationDto): void {
        const navigate = () => {
            if (item.route) void this.router.navigateByUrl(item.route);
        };
        if (item.isRead) {
            navigate();
            return;
        }

        this.notificationApi.markRead(item.id).subscribe({
            next: () => {
                this.notifications.update((items) => items.map((current) => (current.id === item.id ? { ...current, isRead: true } : current)));
                this.unreadCount.update((count) => Math.max(0, count - 1));
                if (this.selectedNotificationState() === 'unread') {
                    this.notifications.update((items) => items.filter((current) => current.id !== item.id));
                }
                navigate();
            },
            error: navigate
        });
    }

    markAllRead(): void {
        this.notificationApi.markAllRead().subscribe({ next: () => this.loadNotifications() });
    }

    archiveNotification(event: Event, item: WfmNotificationDto): void {
        event.stopPropagation();
        this.notificationApi.archive(item.id).subscribe({ next: () => this.loadNotifications() });
    }

    onMenuButtonClick(): void {
        this.layoutService.toggleMenu();
    }
    showRightMenu(): void {
        this.layoutService.toggleRightMenu();
    }
    onConfigButtonClick(): void {
        this.layoutService.toggleConfigSidebar();
    }
    toggleSearchBar(): void {
        this.layoutService.toggleSearchBar();
    }
    logout(): void {
        this.authService.logout();
        void this.router.navigate(['/auth/login']);
    }
}
